import { createListenerMiddleware } from '@reduxjs/toolkit'
import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit'
import RtkQueryService from '@/lib/rtkQuery'

/**
 * Bridges the app's existing "a write succeeded" signal to the RTK Query cache.
 *
 * DAL action handlers already dispatch incrementGridRefreshSeq (global) or
 * incrementGridRefreshSeqForDts(dtsId) (targeted) after every successful
 * create/update/delete — that is what refreshes the grids. This listener piggy-
 * backs on the same actions to invalidate the cached DAL lists, so action
 * enablement that depends on data state (e.g. Edit becoming available once a
 * record exists) refetches immediately instead of serving a stale entry for up
 * to keepUnusedDataFor. A currently-mounted DAL query refetches right away;
 * unmounted entries are dropped so the next tab visit fetches fresh.
 *
 * Centralised here (rather than at each of the ~10 dispatch sites in the DAL
 * action registries) so future action handlers get invalidation for free.
 *
 * The actions are matched by type string, NOT by importing the sduiSlice action
 * creators: sduiSlice's module graph (ObjService -> ComponentRegistry ->
 * BaseService -> '@/store') circles back into storeSetup, which would read this
 * listener's binding before it is initialised. The listener test dispatches the
 * real action creators, so a slice rename that breaks these strings fails CI.
 */
const GRID_REFRESH_GLOBAL = 'sdui/incrementGridRefreshSeq'
const GRID_REFRESH_FOR_DTS = 'sdui/incrementGridRefreshSeqForDts'

const isGridRefreshForDts = (
    action: UnknownAction,
): action is PayloadAction<number> => action.type === GRID_REFRESH_FOR_DTS

export const dalInvalidationListener = createListenerMiddleware()

dalInvalidationListener.startListening({
    predicate: (action) =>
        action.type === GRID_REFRESH_GLOBAL || isGridRefreshForDts(action),
    effect: (action, listenerApi) => {
        const tags = isGridRefreshForDts(action)
            ? [{ type: 'Dal' as const, id: action.payload }]
            : ['Dal' as const]
        listenerApi.dispatch(RtkQueryService.util.invalidateTags(tags))
    },
})
